import type { ReactNode } from 'react'
import { looksLikeAudioFence, parseAudioFenceYaml, type ParsedAudioFence } from '@/lib/audio-fence'
import { isBasecampDraftFenceLanguage } from '@/lib/basecamp-draft-fence'

/** Extract plain text from React children (for headings, code blocks, etc.). */
export function childrenToText(children: ReactNode): string {
  if (typeof children === 'string') return children
  if (Array.isArray(children)) return children.map(childrenToText).join('')
  if (children !== null && typeof children === 'object' && !Array.isArray(children)) {
    const el = children as { props?: { children?: ReactNode } }
    if (el.props) return childrenToText(el.props.children)
  }
  return ''
}

/** If `pre` wraps a fenced `mermaid` block, return the source text. */
export function getMermaidSourceFromPreChildren(children: ReactNode): string | null {
  const nodes = Array.isArray(children) ? children : [children]
  for (const node of nodes) {
    if (!node || typeof node !== 'object' || !('props' in node)) continue
    const props = (node as { props?: { className?: string; children?: ReactNode } }).props
    if (!props?.className?.includes('language-mermaid')) continue
    return childrenToText(props.children).replace(/\n$/, '')
  }
  return null
}

/**
 * If `pre` wraps a Knowledge Hub `yaml review-changelog` fence, return the source.
 * Remark keeps only the first info word, so the class is `language-yaml`.
 */
export function getReviewChangelogSourceFromPreChildren(children: ReactNode): string | null {
  const nodes = Array.isArray(children) ? children : [children]
  for (const node of nodes) {
    if (!node || typeof node !== 'object' || !('props' in node)) continue
    const props = (node as { props?: { className?: string; children?: ReactNode } }).props
    const cls = props?.className || ''
    const text = childrenToText(props?.children).replace(/\n$/, '')
    const isFence =
      cls.includes('language-review-changelog') ||
      (cls.includes('language-yaml') && /entries:\s*\n/i.test(text) && /action:/i.test(text))
    if (!isFence) continue
    return text
  }
  return null
}

/** If `pre` wraps a fenced `html` block, return the source text. */
export function getHtmlSourceFromPreChildren(children: ReactNode): string | null {
  const nodes = Array.isArray(children) ? children : [children]
  for (const node of nodes) {
    if (!node || typeof node !== 'object' || !('props' in node)) continue
    const props = (node as { props?: { className?: string; children?: ReactNode } }).props
    if (!props?.className?.includes('language-html')) continue
    return childrenToText(props.children).replace(/\n$/, '')
  }
  return null
}

/** If `pre` wraps a STATUS-BOARD fence (`yaml status-board` or yaml + hot_pickups), return source. */
export function getStatusBoardSourceFromPreChildren(children: ReactNode): string | null {
  const nodes = Array.isArray(children) ? children : [children]
  for (const node of nodes) {
    if (!node || typeof node !== 'object' || !('props' in node)) continue
    const props = (node as { props?: { className?: string; children?: ReactNode } }).props
    const cls = props?.className || ''
    const text = childrenToText(props?.children).replace(/\n$/, '')
    const lang = cls.replace(/^language-/, '')
    const looksLike =
      cls.includes('language-status-board') ||
      (cls.includes('language-yaml') &&
        /hot_pickups\s*:/i.test(text) &&
        /last_board_update\s*:/i.test(text))
    if (!looksLike && !(lang.includes('status-board') && text)) continue
    return text
  }
  return null
}

/** If `pre` wraps a `yaml basecamp-draft` fence, return the fence body. */
export function getBasecampDraftSourceFromPreChildren(children: ReactNode): {
  source: string
  language: string
} | null {
  const nodes = Array.isArray(children) ? children : [children]
  for (const node of nodes) {
    if (!node || typeof node !== 'object' || !('props' in node)) continue
    const props = (node as { props?: { className?: string; children?: ReactNode } }).props
    const cls = props?.className || ''
    const text = childrenToText(props?.children).replace(/\n$/, '')
    if (!isBasecampDraftFenceLanguage(cls, text)) continue
    const lang = cls.match(/language-([\w-]+)/)?.[1] || cls
    return { source: text, language: lang }
  }
  return null
}

/** If `pre` wraps a `yaml audio` / `audio` fence, return the parsed player source. */
export function getAudioSourceFromPreChildren(children: ReactNode): ParsedAudioFence | null {
  const nodes = Array.isArray(children) ? children : [children]
  for (const node of nodes) {
    if (!node || typeof node !== 'object' || !('props' in node)) continue
    const props = (node as { props?: { className?: string; children?: ReactNode } }).props
    const text = childrenToText(props?.children).replace(/\n$/, '')
    if (!looksLikeAudioFence(props?.className, text)) continue
    return parseAudioFenceYaml(text)
  }
  return null
}
